import { useCallback, useMemo, useState } from 'react';

const isoDateOk = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v);

const parseNum = (v) => {
  if (!v) return null;
  const n = parseFloat(String(v).replace(/[^0-9.\-]/g, ''));
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null;
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const INITIAL = () => ({
  vendor: '', date: todayISO(),
  subtotal: '', gst: '', qst: '',
  total: '', totalTouched: false,
  category: null, notes: '',
});

/**
 * Encapsulates the ManualEntryModal form state, auto-total logic, and validation.
 * Behavior is intentionally identical to the previous inline version.
 */
export function useManualEntryForm() {
  const [form, setForm] = useState(INITIAL);

  const setField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const setTotal = useCallback((v) => {
    setForm((prev) => ({ ...prev, total: v, totalTouched: true }));
  }, []);

  const reset = useCallback(() => setForm(INITIAL()), []);

  const autoTotal = useMemo(() => {
    const s = parseNum(form.subtotal) ?? 0;
    const g = parseNum(form.gst) ?? 0;
    const q = parseNum(form.qst) ?? 0;
    const sum = s + g + q;
    return sum > 0 ? sum.toFixed(2) : '';
  }, [form.subtotal, form.gst, form.qst]);

  const totalDisplay = form.totalTouched ? form.total : autoTotal;
  const effectiveTotal = parseNum(totalDisplay);

  const errors = useMemo(() => {
    const e = {};
    if (!form.vendor.trim()) e.vendor = 'Required';
    if (!isoDateOk(form.date)) e.date = 'Invalid date';
    if (parseNum(form.subtotal) === null) e.subtotal = 'Required';
    if (effectiveTotal === null) e.total = 'Required';
    if (!form.category) e.category = 'Required';
    return e;
  }, [form.vendor, form.date, form.subtotal, form.category, effectiveTotal]);

  const isValid = Object.keys(errors).length === 0;

  const toPayload = useCallback(() => ({
    vendor: { value: form.vendor.trim(), confidence: 1.0 },
    date: { value: form.date, confidence: 1.0 },
    subtotal: { value: parseNum(form.subtotal), confidence: 1.0 },
    gst: { value: parseNum(form.gst), confidence: 1.0 },
    qst: { value: parseNum(form.qst), confidence: 1.0 },
    combined_tax: { value: null, confidence: 0.0 },
    total: { value: effectiveTotal, confidence: 1.0 },
    currency: 'CAD',
    num_images_used: 0,
    category: form.category,
    notes: form.notes.trim() || null,
    source: 'manual',
    was_edited: false,
  }), [form, effectiveTotal]);

  return {
    form, setField, setTotal, reset,
    autoTotal, totalDisplay, effectiveTotal,
    errors, isValid, isoDateOk,
    toPayload,
  };
}
